'use client';

import { useEffect, useRef } from 'react';

import { Spinner } from '@/components';
import { useKycSocket, useKycValidation, useTimer } from '@/hooks';
import { cleanupExatoDocCheck } from '@/utils/exato';

import { RECOVERY_MESSAGES } from '../../constants';

type RecoveryValidatingProps = {
  customerId: string;
  onValidationSuccess: () => void;
  onValidationError: () => void;
  onValidationTimeout: () => void;
};

const FIVE_MINUTES = 5 * 60 * 1000;

export const RecoveryValidating = ({
  customerId,
  onValidationSuccess,
  onValidationError,
  onValidationTimeout,
}: RecoveryValidatingProps) => {
  const hasStartedRef = useRef(false);
  const { cpf } = useKycValidation({ customerId });
  const { time, formatted, start } = useTimer({
    duration: FIVE_MINUTES,
    manual: true,
  });

  const { isConnected, startValidation, stopValidation } = useKycSocket({
    cpf,
    onValidationSuccess,
    onValidationError,
    onValidationTimeout,
  });

  useEffect(() => {
    if (!cpf || hasStartedRef.current) return;

    hasStartedRef.current = true;
    start();

    if (process.env.NEXT_PUBLIC_BYPASS_KYC === 'true') {
      const timer = setTimeout(() => {
        cleanupExatoDocCheck();
        onValidationSuccess();
      }, 1500);

      return () => {
        clearTimeout(timer);
        cleanupExatoDocCheck();
        stopValidation();
        hasStartedRef.current = false;
      };
    }

    startValidation();

    return () => {
      stopValidation();
      hasStartedRef.current = false;
    };
  }, [cpf, onValidationSuccess, start, startValidation, stopValidation]);

  const progress = ((FIVE_MINUTES - time) / FIVE_MINUTES) * 100;

  return (
    <div className="flex flex-col items-center gap-5 p-2 text-center">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          {RECOVERY_MESSAGES.KYC_TITLE}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {RECOVERY_MESSAGES.KYC_VALIDATING}
        </p>
      </div>

      <div className="flex w-full flex-col gap-4 rounded-xl bg-foreground/5 p-4">
        <div className="flex items-center justify-center gap-3">
          <Spinner className="h-5 w-5" />
          <span className="text-sm font-semibold text-foreground">Validando...</span>
        </div>

        <div className="h-2 w-full rounded-full bg-foreground/10">
          <div
            className="h-2 rounded-full bg-primary transition-all duration-1000"
            style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <span>{isConnected ? 'Conectado' : 'Conectando...'}</span>
          <span>Expira em {formatted}</span>
        </div>
      </div>
    </div>
  );
};
